import { useEffect, useId, useMemo, useRef, useState, type JSX } from "react";
import type { DataIssue, DataIssueField } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button } from "../../design/components/Button";
import { Reason } from "../../design/components/Reason";
import { Table } from "../../design/components/Table";
import { messageForError } from "../../i18n/errors";
import { formatCount } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { formatPhoneForDisplay } from "../lead-display";
import { allFixableIssues, dataIssuesPagesQuery, distinctIssues, useDismissIssue, useFixIssues, type FixOutcome } from "./cleanup-queries";
import type { CleanupOutcome } from "./DataCleanupDialog";

// «Telefoni ed email (N)» (piano M3 Task 18, D12; mock-up `lead.html`, `renderClean` di `initLeadPage`): una riga per ogni
// dato da sistemare, con il Lead (il nome porta alla scheda), il campo, il valore di adesso, il problema e la proposta, che è
// il valore giusto, lo spostamento nell'altro posto del telefono oppure «Da verificare con il Lead». «Correggi» manda la
// chiave della riga con la versione letta (la proposta la ricalcola il server); «Correggi tutte le proposte (N)» chiede una
// seconda volta («Conferma: correggi N», con l'avviso) e poi le manda tutte, anche quelle non ancora in vista, a gruppi di
// 500. Una riga che il server non corregge (il Lead cambiato, il numero già di un altro Lead, un'email che la scheda non
// accetta) si conta nell'esito, e l'elenco si rilegge. «Tieni così» la toglie finché il valore non cambia.

const PHONE_FIELDS: ReadonlySet<DataIssueField> = new Set(["mobile", "landline", "secondMobile"]);

/** Un valore della riga come lo si legge: i telefoni divisi in gruppi, le email come sono. */
function shown(field: DataIssueField, value: string): string {
  return PHONE_FIELDS.has(field) ? formatPhoneForDisplay(value) : value;
}

/** Il nome del Lead di una riga: il nome, altrimenti il numero della riga, altrimenti «Apri il Lead». */
function rowName(t: Translate, row: DataIssue): string {
  return row.displayName?.trim() || (PHONE_FIELDS.has(row.field) ? formatPhoneForDisplay(row.current) : t("dataCleanup.openLead"));
}

/**
 * L'esito di una correzione: il campo e il Lead se se n'è corretta una sola, altrimenti quante; poi quante non si sono
 * potute correggere (il server le ha saltate, o non sono partite dopo un rifiuto) e il rifiuto, se c'è stato. È un avviso
 * quando non si è corretto nulla o quando un rifiuto ha fermato le richieste rimaste.
 */
function fixText(t: Translate, locale: Locale, sent: readonly DataIssue[], result: FixOutcome): CleanupOutcome {
  const parts: string[] = [];
  if (result.fixed === 1) {
    const skipped = new Set(result.skipped.map((entry) => entry.key));
    const done = sent.slice(0, sent.length - result.unsent).find((row) => !skipped.has(row.key));
    if (done) parts.push(t("dataCleanup.fixed", { field: t(`dataCleanup.issueFields.${done.field}`).toLocaleLowerCase(locale), name: rowName(t, done) }));
  } else if (result.fixed > 1) {
    parts.push(t("dataCleanup.fixedMany", { count: formatCount(locale, result.fixed) }));
  }
  const notFixed = result.skipped.length + result.unsent;
  if (notFixed > 0) parts.push(notFixed === 1 ? t("dataCleanup.fixedSkippedOne") : t("dataCleanup.fixedSkipped", { count: formatCount(locale, notFixed) }));
  if (result.stoppedBy !== null) parts.push(` ${messageForError(t, result.stoppedBy)}`);
  return { tone: result.fixed > 0 && result.stoppedBy === null ? "success" : "warning", text: parts.join("").trim() };
}

type Props = {
  tab: TabContext;
  view: string | null;
  onOutcome: (outcome: CleanupOutcome | null) => void;
};

export function DataIssuesTab({ tab, view, onOutcome }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const warningId = useId();
  const pages = useInfiniteQuery({ ...dataIssuesPagesQuery(tab, view), placeholderData: keepPreviousData });
  const fix = useFixIssues(tab);
  const dismiss = useDismissIssue(tab);
  // «Correggi tutte le proposte» premuto una volta: aspetta «Conferma: correggi N».
  const [armed, setArmed] = useState(false);
  // Le righe non ancora in vista si stanno leggendo per correggerle tutte.
  const [collecting, setCollecting] = useState(false);
  const busy = fix.isPending || dismiss.isPending || collecting;
  // La lettura delle righe non ancora in vista si ferma quando la scheda (o la finestra) si chiude.
  const reading = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    reading.current = controller;
    return () => controller.abort();
  }, []);
  const rows = useMemo(() => distinctIssues(pages.data?.pages ?? []), [pages.data]);
  const fixable = pages.data?.pages[0]?.fixable ?? 0;

  async function runFix(targets: readonly DataIssue[]) {
    try {
      const result = await fix.mutateAsync(targets);
      onOutcome(fixText(t, locale, targets, result));
    } catch (error) {
      onOutcome({ tone: "warning", text: messageForError(t, error) });
    }
  }

  function fixOne(row: DataIssue) {
    if (busy) return;
    setArmed(false);
    onOutcome(null);
    void runFix([row]);
  }

  async function fixAll() {
    if (busy) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onOutcome(null);
    setCollecting(true);
    const signal = reading.current?.signal;
    let targets: DataIssue[];
    try {
      targets = await allFixableIssues(tab, { rows, complete: !pages.hasNextPage }, signal);
    } catch (error) {
      // Chiusa la scheda, nessun esito da dire: non è partita nessuna correzione.
      if (!signal?.aborted) onOutcome({ tone: "warning", text: messageForError(t, error) });
      return;
    } finally {
      setCollecting(false);
    }
    // Nel frattempo non è rimasta nessuna proposta: l'elenco si rilegge e lo dice da sé.
    if (signal?.aborted) return;
    if (targets.length === 0) {
      void pages.refetch();
      return;
    }
    await runFix(targets);
  }

  async function keep(row: DataIssue) {
    if (busy) return;
    setArmed(false);
    onOutcome(null);
    try {
      await dismiss.mutateAsync({ key: row.key });
      onOutcome({ tone: "success", text: t("dataCleanup.kept") });
    } catch (error) {
      onOutcome({ tone: "warning", text: messageForError(t, error) });
    }
  }

  if (pages.isError && !pages.data) {
    return (
      <div className="g-cleanup__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, pages.error)}
        </Reason>
        <Button onClick={() => void pages.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  }
  if (!pages.data) return <p role="status">{t("common.loading")}</p>;
  if (rows.length === 0 && !pages.isPlaceholderData) return <p className="g-cleanup__empty">{t("dataCleanup.noIssues")}</p>;

  const count = formatCount(locale, fixable);
  return (
    <div className="g-cleanup__issues">
      {fixable > 0 ? (
        <div className="g-cleanup__fix-all">
          <Button
            variant={armed ? "primary" : "secondary"}
            onClick={() => void fixAll()}
            disabled={busy}
            aria-busy={collecting || fix.isPending ? "true" : undefined}
            aria-describedby={armed ? warningId : undefined}
          >
            {armed ? t("dataCleanup.fixAllConfirm", { count }) : t("dataCleanup.fixAll", { count })}
          </Button>
          {armed ? (
            <Reason id={warningId} tone="warning">
              {t("dataCleanup.fixAllWarning")}
            </Reason>
          ) : null}
        </div>
      ) : null}
      <Table className="g-issues__table" caption={t("dataCleanup.issuesCaption")} busy={pages.isFetching || busy}>
          <thead>
            <tr>
              <th scope="col">{t("dataCleanup.issueColumns.lead")}</th>
              <th scope="col">{t("dataCleanup.issueColumns.field")}</th>
              <th scope="col">{t("dataCleanup.issueColumns.now")}</th>
              <th scope="col">{t("dataCleanup.issueColumns.problem")}</th>
              <th scope="col">{t("dataCleanup.issueColumns.proposal")}</th>
              <th scope="col">
                <span className="g-visually-hidden">{t("dataCleanup.issueColumns.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <IssueRow key={row.key} t={t} row={row} busy={busy} onFix={() => fixOne(row)} onKeep={() => void keep(row)} />
            ))}
          </tbody>
      </Table>
      {pages.hasNextPage ? (
        <div>
          <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage} aria-busy={pages.isFetchingNextPage ? "true" : undefined}>
            {t("dataCleanup.more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type RowProps = { t: Translate; row: DataIssue; busy: boolean; onFix: () => void; onKeep: () => void };

/** Una riga: il Lead (nome e salone), il campo, adesso, il problema, la proposta e le azioni. */
function IssueRow({ t, row, busy, onFix, onKeep }: RowProps) {
  const name = rowName(t, row);
  const salon = row.salonName?.trim();
  let proposal: JSX.Element | string;
  if (row.proposal === null) proposal = t("dataCleanup.verify");
  else if (row.move !== null) proposal = t("dataCleanup.moveTo", { slot: t(`dataCleanup.issueFields.${row.move}`), value: formatPhoneForDisplay(row.proposal) });
  else proposal = <strong>{shown(row.field, row.proposal)}</strong>;
  return (
    <tr>
      <th scope="row">
        <Link to="/lead/$leadId" params={{ leadId: row.leadId }}>
          {name}
        </Link>
        {salon && salon !== name ? <span className="g-table__sub">{salon}</span> : null}
      </th>
      <td>{t(`dataCleanup.issueFields.${row.field}`)}</td>
      <td className="g-issues__value">{shown(row.field, row.current)}</td>
      <td>{t(`dataCleanup.problems.${row.problem}`)}</td>
      <td className="g-issues__value">{proposal}</td>
      <td>
        <div className="g-issues__actions">
          {row.proposal !== null ? (
            <Button onClick={onFix} disabled={busy}>
              {t("dataCleanup.fix")}
            </Button>
          ) : null}
          <Button onClick={onKeep} disabled={busy}>
            {t("dataCleanup.keep")}
          </Button>
        </div>
      </td>
    </tr>
  );
}
